import {
  ClientTeamMemberAutoComplete as SharedClientTeamMemberAutoComplete,
  ClientTeamMemberFooter,
} from '@ths/shared';
import { useMemo } from 'react';

import {
  ClientTeamMemberOption,
  useClientClientTeamMemberSelector,
} from '../../hooks/useClientClientTeamMemberSelector';
import { looksLikeDODID } from '../../utilities/validators';

type FormClientTeamMemberSelectorProps = {
  onAdd: (guest: ClientTeamMemberOption | null) => void;
  onCreateGuest?: (query: string) => void;
  initialQuery?: string;
  disabled?: boolean;
  labelText: string;
  placeholder: string;
  value?: ClientTeamMemberOption | null;
};

const ClientTeamMemberAutoComplete = ({
  disabled,
  initialQuery,
  onAdd,
  onCreateGuest,
  value = null,
  labelText,
  placeholder,
}: FormClientTeamMemberSelectorProps) => {
  // Use hook for data fetching and wrapping
  const { wrappedGuests, query, onQueryChange, loading, unknownGuest } =
    useClientClientTeamMemberSelector({
      initialQuery,
    });

  const handleAdd = (guest: ClientTeamMemberOption | null) => {
    if (!guest) {
      // Handle the case where user typed a query but didn't select a guest
      if (query.trim()) {
        if (looksLikeDODID(query)) {
          onAdd({ edipi: query } as ClientTeamMemberOption);
        } else {
          // For non-DoD ID queries (names), create a guest without EDIPI
          const nameParts = query.trim().split(/\s+/);
          const firstName = nameParts[0] || '';
          const lastName =
            nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
          onAdd({ firstName, lastName } as ClientTeamMemberOption);
        }
      } else {
        onAdd(null);
      }
    } else {
      onAdd(guest);
    }
  };

  // Filter out Unknown Guest from dropdown since it has a dedicated footer button
  // But keep it if it's the currently selected value so it displays in the input
  const filteredGuests = useMemo(() => {
    const guestsWithoutUnknown = wrappedGuests.filter(
      (guest) => !guest.value.unknownGuest,
    );

    // If unknown guest is selected, include it so the shared component can display it
    if (value?.unknownGuest) {
      const unknownGuestOption = wrappedGuests.find(
        (guest) => guest.value.unknownGuest,
      );
      if (unknownGuestOption) {
        return [unknownGuestOption, ...guestsWithoutUnknown];
      }
    }

    return guestsWithoutUnknown;
  }, [wrappedGuests, value]);

  // Conditionally render footer if unknownGuest or onCreateGuest exists
  const footer =
    unknownGuest || onCreateGuest ? (
      <ClientTeamMemberFooter
        onUnknownGuestClick={() => unknownGuest && onAdd(unknownGuest)}
        onCreateClick={() => onCreateGuest?.(query)}
      />
    ) : undefined;

  return (
    <SharedClientTeamMemberAutoComplete
      guests={filteredGuests}
      value={value}
      onChange={handleAdd}
      label={labelText}
      placeholder={placeholder}
      disabled={disabled}
      loading={loading}
      onQueryChange={onQueryChange}
      footer={footer}
    />
  );
};

export default ClientTeamMemberAutoComplete;
